<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import AppointmentsAPI from 'dashboard/api/appointments';
import StatusBadge from 'dashboard/components-next/appointments/StatusBadge.vue';

// Read-only passive widget: lists appointments linked to the current conversation
// (via conversation_display_id) so agents see booking context without leaving the
// conversation. fase-29.
const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const appointments = ref([]);
const loading = ref(false);
let accountGeneration = 0;
let requestId = 0;
let abortController = null;

const currentAccountId = computed(() => {
  const getter = store.getters.getCurrentAccountId;
  return typeof getter === 'function' ? getter() : getter;
});

const normalizeId = value => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const captureContext = () => ({
  accountId: normalizeId(currentAccountId.value),
  conversationId: normalizeId(props.conversationId),
  generation: accountGeneration,
});

const isCurrentContext = context =>
  context.accountId === normalizeId(currentAccountId.value) &&
  context.conversationId === normalizeId(props.conversationId) &&
  context.generation === accountGeneration;

function formatDateTime(iso) {
  return new Intl.DateTimeFormat((locale.value || 'en-US').replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

async function load() {
  const context = captureContext();
  if (!context.accountId || !context.conversationId) {
    appointments.value = [];
    return;
  }
  requestId += 1;
  const currentRequestId = requestId;
  abortController?.abort();
  abortController = new AbortController();
  loading.value = true;
  try {
    const response = await AppointmentsAPI.list(
      { conversation_display_id: context.conversationId },
      context.accountId,
      { signal: abortController.signal }
    );
    if (currentRequestId !== requestId || !isCurrentContext(context)) return;

    appointments.value = (response.data?.data || [])
      .slice()
      .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  } catch {
    if (currentRequestId === requestId && isCurrentContext(context)) {
      appointments.value = [];
    }
  } finally {
    if (currentRequestId === requestId && isCurrentContext(context)) {
      loading.value = false;
    }
  }
}

watch(
  [currentAccountId, () => props.conversationId],
  ([accountId], [previousAccountId] = []) => {
    if (normalizeId(accountId) !== normalizeId(previousAccountId)) {
      accountGeneration += 1;
    }
    requestId += 1;
    abortController?.abort();
    appointments.value = [];
    loading.value = false;
    load();
  },
  { immediate: true }
);

onBeforeUnmount(() => abortController?.abort());
</script>

<template>
  <div class="px-1">
    <div v-if="loading" class="h-10 bg-n-alpha-1 rounded-lg animate-pulse" />
    <p v-else-if="!appointments.length" class="text-sm text-n-slate-11 py-2">
      {{ t('APPOINTMENTS.CONVERSATION_WIDGET.EMPTY') }}
    </p>
    <div v-else class="space-y-1.5">
      <div
        v-for="appt in appointments"
        :key="appt.id"
        class="rounded-lg border border-n-weak bg-n-background p-2.5 text-sm"
      >
        <div class="flex items-center justify-between gap-2 mb-1">
          <StatusBadge :status="appt.status" />
          <span class="text-[11px] text-n-slate-10">
            {{ formatDateTime(appt.scheduled_at) }}
          </span>
        </div>
        <p class="font-semibold text-n-slate-12 truncate">
          {{ appt.service?.name }}
        </p>
        <p
          v-if="appt.professional"
          class="text-[11px] text-n-slate-10 truncate"
        >
          {{ appt.professional.name }}
        </p>
      </div>
    </div>
  </div>
</template>
